// src/app/companies/services/company-upload.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

import { CompanyImportRow } from '../models/company-import-row.model';

export interface CurrentUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: { id: number; name: string }[];
  jobTitleRoles: any[];
  departmentRoles: any[];
  jobTitle: string | null;
  reportTo: string | null;
}

export interface CompanyDto {
  name: string;
  description: string | null;
  isSubtractor: boolean;
}

export interface Company {
  id: number;
  name: string;
  description: string | null;
  isSubtractor: boolean;
}

export interface ApiResponse<T> {
  status: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class CompanyUploadService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  uploadRows(rows: CompanyImportRow[]): Observable<Company[]> {
    const payload: CompanyDto[] = rows.map((r) => this.mapRowToApi(r));
    return this.http
      .post<ApiResponse<Company[]>>(`${this.baseUrl}/api/companies/multiple`, payload)
      .pipe(map((res) => res.data));
  }

  getCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${this.baseUrl}/api/account/get-current-user`);
  }

  private mapRowToApi(row: CompanyImportRow): CompanyDto {
    const name = (row.Name ?? '').toString().trim();
    const description =
      row.Description === null || row.Description === undefined
        ? null
        : row.Description.toString().trim();

    return {
      name,
      description: description?.length ? description : null,
      // IMPORTANT: backend field is "isSubtractor" (as you described)
      isSubtractor: this.parseBoolean(row.IsSubtractor),
    };
  }

  private parseBoolean(value: CompanyImportRow['IsSubtractor']): boolean {
    if (value === null || value === undefined) return false;

    if (typeof value === 'boolean') return value;

    if (typeof value === 'number') return value !== 0;

    const s = String(value).trim().toLowerCase();
    if (['true', 't', 'yes', 'y', '1'].includes(s)) return true;
    if (['false', 'f', 'no', 'n', '0'].includes(s)) return false;

    // if it’s anything else, default false; template should be strict anyway
    return false;
  }
}